/**
 * The floating-island presentation (AD-840; docs/architecture/island-edges.md) —
 * the PURE half: geometry arrays, instance transforms and the cloud texture, all
 * plain data. It imports no `three` and does no I/O, so `node --test` pins it;
 * `islandMesh.ts` turns it into meshes.
 *
 * What a zone is drawn as, from the walkable coastline the server shipped
 * (`collision.outline`) and the knobs it resolved (`world.island`):
 *
 * - **Top** — the zone's ground, cut to the rim polygon (the coastline pushed out
 *   `rimMargin` metres, with a little lip jitter), as a ringed fan in the zone's
 *   own ground material. Replaces the bounds rectangle.
 * - **Underside** — a faceted rock mass hanging below the rim: an earth lip, a
 *   short cliff band, then strata tapering to hanging lobes `undersideDepth`
 *   below. Vertex-coloured strata, flat-shaded (the Faceted Angular rock language).
 * - **Crags + debris** — instanced faceted rocks along the lip (breaking the
 *   silhouette) and a few floating below and beside it.
 * - **Roots** — thin hanging roots under the lip (desktop only).
 * - **Cloud sea** — an opaque cloud floor `cloudSeaDepth` below the rim and, on
 *   desktop, a drifting translucent wisp layer between, both fogged into the
 *   sky's horizon colour so the world ends in cloud, not in nothing.
 * - **Distant islands** — a few small islands out in the haze for depth.
 *
 * The LOW profile (mobile tier / `?quality=low`) keeps the silhouette and drops
 * the costly parts: fewer underside rings, half the crags, no roots, no wisp
 * layer (it is the only blended full-screen layer), two distant islands.
 */

import type { IslandOutline, IslandWire, Vec2 } from '@great-cloud/shared/client';
import {
  insideOutline,
  islandRimPolygon,
  islandRng,
  nearestOnOutline,
} from '@great-cloud/shared/client';

/** What the scene plan carries for an island zone (plain data). */
export interface IslandPlan {
  /** The walkable coastline (server-shipped, cm-quantised). */
  outline: IslandOutline;
  /** The drawn rim: the coastline pushed out `rimMargin` metres (before lip jitter). */
  rim: Vec2[];
  /** The resolved knobs. */
  wire: IslandWire;
}

/** Ground height under a world point (the zone's surface; flat ⇒ 0). */
export type HeightAt = (x: number, z: number) => number;

/** A detail level: `lite` is the low profile. */
export interface IslandDetail {
  lite: boolean;
}

export interface MeshArrays {
  positions: Float32Array;
  indices: Uint32Array;
  /** Per-vertex linear RGB (absent for the top, which uses the ground material). */
  colors?: Float32Array;
  /** Per-vertex normals; absent ⇒ computed from the triangles (a relief top carries the uncut mesh's). */
  normals?: Float32Array;
}

/** One instanced rock/root: position, non-uniform scale, yaw/tilt, and a colour. */
export interface IslandInstance {
  x: number;
  y: number;
  z: number;
  sx: number;
  sy: number;
  sz: number;
  rotY: number;
  tilt: number;
  color: readonly [number, number, number];
}

/** How far a crag's centre stays outside the walkable line, metres (never inside the walk). */
export const CRAG_CLEARANCE_M = 0.45;

/** Underside ring profile: [depth fraction, radius fraction toward the centre]. */
const UNDERSIDE_PROFILE_FULL: ReadonlyArray<readonly [number, number]> = [
  [0.0, 1.0],
  [0.03, 1.035],
  [0.1, 1.06],
  [0.2, 1.03],
  [0.32, 0.94],
  [0.46, 0.8],
  [0.62, 0.6],
  [0.8, 0.36],
  [1.0, 0.1],
];
const UNDERSIDE_PROFILE_LITE: ReadonlyArray<readonly [number, number]> = [
  [0.0, 1.0],
  [0.06, 1.05],
  [0.22, 1.0],
  [0.5, 0.7],
  [1.0, 0.12],
];

/** Top-fan ring fractions (the outer ring is the rim itself). */
const TOP_RINGS = [0.45, 0.75, 0.92, 1] as const;

/** Linear-ish sRGB palette for the strata (0..1 floats; three converts per its colour space). */
const EARTH: readonly [number, number, number] = [0.27, 0.2, 0.13];
const STRATA: ReadonlyArray<readonly [number, number, number]> = [
  [0.42, 0.37, 0.31],
  [0.33, 0.29, 0.26],
  [0.47, 0.41, 0.34],
  [0.3, 0.28, 0.27],
  [0.39, 0.35, 0.31],
];
const DEEP: readonly [number, number, number] = [0.3, 0.32, 0.38];
const ROOT: readonly [number, number, number] = [0.24, 0.17, 0.11];
const MOSS: readonly [number, number, number] = [0.33, 0.4, 0.24];

/** Build the plan's island from the shipped coastline + knobs. */
export function islandPlanOf(outline: IslandOutline, wire: IslandWire): IslandPlan {
  return { outline, rim: islandRimPolygon(outline, wire.rimMargin), wire };
}

function mix3(
  a: readonly [number, number, number],
  b: readonly [number, number, number],
  t: number,
): [number, number, number] {
  const u = t < 0 ? 0 : t > 1 ? 1 : t;
  return [a[0] + (b[0] - a[0]) * u, a[1] + (b[1] - a[1]) * u, a[2] + (b[2] - a[2]) * u];
}

/** Lip jitter: the drawn rim wobbles OUTWARD only (0..0.45 m), so it never crosses the walk. */
export function lipRim(plan: IslandPlan): Vec2[] {
  const rng = islandRng(plan.wire.seed ^ 0x51ed270b);
  const { cx, cz } = plan.outline;
  return plan.rim.map((p) => {
    const dx = p.x - cx;
    const dz = p.z - cz;
    const len = Math.sqrt(dx * dx + dz * dz) || 1;
    const out = rng() * 0.45;
    return { x: p.x + (dx / len) * out, z: p.z + (dz / len) * out };
  });
}

/** Ray-cast point-in-polygon (even-odd) — presentation helper. */
export function insidePolygon(poly: readonly Vec2[], x: number, z: number): boolean {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const a = poly[i] as Vec2;
    const b = poly[j] as Vec2;
    if (a.z > z !== b.z > z && x < a.x + ((z - a.z) * (b.x - a.x)) / (b.z - a.z)) inside = !inside;
  }
  return inside;
}

/**
 * Whether a drawn object at (x, z) with `radius` sits on the island top: its
 * centre inside the rim polygon pulled `radius` toward the centre. Scatter,
 * placeholder rocks and ground cover are filtered by it so nothing floats over
 * the cloud sea.
 */
export function onIslandTop(plan: IslandPlan, x: number, z: number, radius = 0): boolean {
  const { cx, cz } = plan.outline;
  const dx = x - cx;
  const dz = z - cz;
  const len = Math.sqrt(dx * dx + dz * dz);
  if (len < 1e-9) return true;
  const px = x + (dx / len) * radius;
  const pz = z + (dz / len) * radius;
  return insidePolygon(plan.rim, px, pz);
}

/**
 * The island top: a ringed fan from the centre to the lip rim, at the surface
 * height. Drawn in the zone's ground material (world-space), so it carries the
 * same palette and detail the rectangle did.
 */
export function islandTopArrays(plan: IslandPlan, heightAt: HeightAt): MeshArrays {
  const rim = lipRim(plan);
  const n = rim.length;
  const { cx, cz } = plan.outline;
  const rings = TOP_RINGS.length;
  const positions = new Float32Array((1 + n * rings) * 3);
  positions[0] = cx;
  positions[1] = heightAt(cx, cz);
  positions[2] = cz;
  for (let r = 0; r < rings; r += 1) {
    const f = TOP_RINGS[r] as number;
    for (let i = 0; i < n; i += 1) {
      const p = rim[i] as Vec2;
      const x = cx + (p.x - cx) * f;
      const z = cz + (p.z - cz) * f;
      const o = (1 + r * n + i) * 3;
      positions[o] = x;
      positions[o + 1] = heightAt(x, z);
      positions[o + 2] = z;
    }
  }
  const indices: number[] = [];
  // Counter-clockwise seen from above (+y normal): centre, next, this.
  for (let i = 0; i < n; i += 1) {
    const a = 1 + i;
    const b = 1 + ((i + 1) % n);
    indices.push(0, b, a);
  }
  for (let r = 0; r < rings - 1; r += 1) {
    for (let i = 0; i < n; i += 1) {
      const a = 1 + r * n + i;
      const b = 1 + r * n + ((i + 1) % n);
      const c = 1 + (r + 1) * n + i;
      const d = 1 + (r + 1) * n + ((i + 1) % n);
      indices.push(a, b, d, a, d, c);
    }
  }
  return { positions, indices: Uint32Array.from(indices) };
}

/** A smooth periodic noise over the angle (harmonics 1..5), in [-1, 1]. */
function angularNoise(seed: number): (t: number) => number {
  const rng = islandRng(seed);
  const terms: { k: number; amp: number; phase: number }[] = [];
  let sum = 0;
  for (let k = 1; k <= 5; k += 1) {
    const amp = (0.4 + 0.6 * rng()) / k;
    terms.push({ k, amp, phase: rng() * Math.PI * 2 });
    sum += amp;
  }
  return (t) => {
    let v = 0;
    for (const term of terms) v += term.amp * Math.cos(term.k * t + term.phase);
    return v / sum;
  };
}

/**
 * The underside: rings below the lip rim, each pulled toward the centre by the
 * profile and jittered, with the depth varying around the island (hanging lobes),
 * closed by a tip under the centre. Vertex colours: an earth lip, banded strata,
 * cooling into the haze toward the bottom.
 */
export function islandUndersideArrays(
  plan: IslandPlan,
  heightAt: HeightAt,
  detail: IslandDetail,
): MeshArrays {
  const rim = lipRim(plan);
  const n = rim.length;
  const { cx, cz } = plan.outline;
  const depth = plan.wire.undersideDepth;
  const profile = detail.lite ? UNDERSIDE_PROFILE_LITE : UNDERSIDE_PROFILE_FULL;
  const rng = islandRng(plan.wire.seed ^ 0x2545f491);
  const lobes = angularNoise(plan.wire.seed ^ 0x68e31da4);
  const bandShift = rng() * 10;
  const rows = profile.length;
  const vCount = rows * n + 1;
  const positions = new Float32Array(vCount * 3);
  const colors = new Float32Array(vCount * 3);
  let maxDepth = 0;
  for (let r = 0; r < rows; r += 1) {
    const [df, rf] = profile[r] as readonly [number, number];
    for (let i = 0; i < n; i += 1) {
      const p = rim[i] as Vec2;
      const t = (i / n) * Math.PI * 2;
      const lobe = 0.72 + 0.42 * lobes(t); // 0.3..1.14 of the depth
      const top = heightAt(p.x, p.z);
      const jitterR = r === 0 ? 0 : (rng() - 0.5) * 0.12 * (r < 3 ? 0.4 : 1);
      const radial = Math.max(0.03, rf + jitterR * rf);
      const y = top - df * depth * lobe - (r === 0 ? 0 : (rng() - 0.3) * 0.5);
      const x = cx + (p.x - cx) * radial;
      const z = cz + (p.z - cz) * radial;
      const o = (r * n + i) * 3;
      positions[o] = x;
      positions[o + 1] = y;
      positions[o + 2] = z;
      maxDepth = Math.max(maxDepth, top - y);
      // Colour: earth at the lip, strata bands below, deep haze at the bottom.
      let c: [number, number, number];
      if (df <= 0.02) c = [...EARTH];
      else {
        const band = Math.floor((top - y) / 2.2 + bandShift + 0.6 * lobes(t * 3)) % STRATA.length;
        const s = STRATA[(band + STRATA.length) % STRATA.length] as readonly [
          number,
          number,
          number,
        ];
        c = mix3(s, DEEP, Math.min(1, df * 1.15));
      }
      // Per-vertex shade so a band never reads as one flat colour (a painted chamfer).
      const shade = 0.78 + rng() * 0.34;
      colors[o] = c[0] * shade;
      colors[o + 1] = c[1] * shade;
      colors[o + 2] = c[2] * shade;
    }
  }
  // The tip under the centre.
  const tip = rows * n * 3;
  positions[tip] = cx;
  positions[tip + 1] = heightAt(cx, cz) - depth * 1.3;
  positions[tip + 2] = cz;
  colors[tip] = DEEP[0];
  colors[tip + 1] = DEEP[1];
  colors[tip + 2] = DEEP[2];
  const indices: number[] = [];
  // Outward-facing (seen from outside/below).
  for (let r = 0; r < rows - 1; r += 1) {
    for (let i = 0; i < n; i += 1) {
      const a = r * n + i;
      const b = r * n + ((i + 1) % n);
      const c = (r + 1) * n + i;
      const d = (r + 1) * n + ((i + 1) % n);
      indices.push(a, d, c, a, b, d);
    }
  }
  const last = (rows - 1) * n;
  const tipIndex = rows * n;
  for (let i = 0; i < n; i += 1) indices.push(last + i, last + ((i + 1) % n), tipIndex);
  return { positions, indices: Uint32Array.from(indices), colors };
}

/** Crags on the lip (breaking the silhouette) + debris floating below and beside it. */
export function islandRockInstances(
  plan: IslandPlan,
  heightAt: HeightAt,
  detail: IslandDetail,
): IslandInstance[] {
  const { cx, cz, points } = plan.outline;
  const rim = plan.rim;
  const n = rim.length;
  const rng = islandRng(plan.wire.seed ^ 0x1b873593);
  let perimeter = 0;
  for (let i = 0; i < n; i += 1) {
    const a = rim[i] as Vec2;
    const b = rim[(i + 1) % n] as Vec2;
    perimeter += Math.sqrt((b.x - a.x) ** 2 + (b.z - a.z) ** 2);
  }
  const density = plan.wire.crags * (detail.lite ? 0.5 : 1);
  const crags = Math.round((perimeter / 3.6) * density);
  const out: IslandInstance[] = [];
  for (let k = 0; k < crags; k += 1) {
    const i = Math.floor(rng() * n) % n;
    const w = points[i] as Vec2;
    const r = rim[i] as Vec2;
    const dx = r.x - cx;
    const dz = r.z - cz;
    const len = Math.sqrt(dx * dx + dz * dz) || 1;
    const ux = dx / len;
    const uz = dz / len;
    const wLen = Math.sqrt((w.x - cx) ** 2 + (w.z - cz) ** 2);
    const tall = rng() < 0.18;
    const s = 0.55 + rng() * (tall ? 1.1 : 1.5);
    const sx = s * (0.8 + rng() * 0.5);
    const sy = s * (tall ? 1.8 + rng() * 0.8 : 0.6 + rng() * 0.5);
    const sz = s * (0.8 + rng() * 0.5);
    // The rock's horizontal reach (unit icosahedron, scaled; a tilt leans it a little more).
    const reach = Math.max(sx, sz) * 1.05;
    let radial = Math.max(wLen + CRAG_CLEARANCE_M + reach, len - 0.6 + rng() * 1.6);
    let x = cx + ux * radial;
    let z = cz + uz * radial;
    // Never into the walk: the WHOLE rock stays CRAG_CLEARANCE_M clear of the coastline —
    // not just along its own ray, where a neighbouring bulge of the coast can still reach it.
    // A crag the player could walk into is an obstacle the server does not know about.
    for (let guard = 0; guard < 24; guard += 1) {
      const near = nearestOnOutline(plan.outline, x, z);
      if (!insideOutline(plan.outline, x, z) && Math.sqrt(near.d2) >= reach + CRAG_CLEARANCE_M)
        break;
      radial += 0.25;
      x = cx + ux * radial;
      z = cz + uz * radial;
    }
    const ground = heightAt(r.x, r.z);
    const shade = 0.7 + rng() * 0.35;
    const base = mix3(
      STRATA[k % STRATA.length] as readonly [number, number, number],
      MOSS,
      rng() * 0.45,
    );
    out.push({
      x,
      y: ground - 0.4 * s + (tall ? s * 0.6 : 0),
      z,
      sx,
      sy,
      sz,
      rotY: rng() * Math.PI * 2,
      tilt: (rng() - 0.5) * 0.5,
      color: [base[0] * shade, base[1] * shade, base[2] * shade],
    });
  }
  const debris = Math.round((detail.lite ? 6 : 16) * Math.min(1, plan.wire.crags + 0.25));
  const depth = plan.wire.undersideDepth;
  for (let k = 0; k < debris; k += 1) {
    const i = Math.floor(rng() * n) % n;
    const r = rim[i] as Vec2;
    const f = 0.9 + rng() * 0.45;
    const s = 0.35 + rng() * 1.5;
    const shade = 0.75 + rng() * 0.3;
    const base = STRATA[(k + 2) % STRATA.length] as readonly [number, number, number];
    out.push({
      x: cx + (r.x - cx) * f,
      y: heightAt(r.x, r.z) - 2.5 - rng() * depth * 0.75,
      z: cz + (r.z - cz) * f,
      sx: s,
      sy: s * (0.6 + rng() * 0.6),
      sz: s * (0.7 + rng() * 0.6),
      rotY: rng() * Math.PI * 2,
      tilt: (rng() - 0.5) * 1.2,
      color: [base[0] * shade, base[1] * shade, base[2] * shade],
    });
  }
  return out;
}

/** Hanging roots under the lip (desktop only; none on the low profile). */
export function islandRootInstances(
  plan: IslandPlan,
  heightAt: HeightAt,
  detail: IslandDetail,
): IslandInstance[] {
  if (detail.lite || !plan.wire.roots) return [];
  const rim = plan.rim;
  const n = rim.length;
  const { cx, cz } = plan.outline;
  const rng = islandRng(plan.wire.seed ^ 0x3c6ef372);
  const count = Math.round(n * 0.4);
  const out: IslandInstance[] = [];
  for (let k = 0; k < count; k += 1) {
    const i = Math.floor(rng() * n) % n;
    const r = rim[i] as Vec2;
    const f = 0.97 + rng() * 0.05;
    const len = 1.2 + rng() * 4.5;
    out.push({
      x: cx + (r.x - cx) * f,
      y: heightAt(r.x, r.z) - 0.4 - len / 2,
      z: cz + (r.z - cz) * f,
      sx: 0.6 + rng() * 0.8,
      sy: len,
      sz: 0.6 + rng() * 0.8,
      rotY: rng() * Math.PI * 2,
      tilt: (rng() - 0.5) * 0.35,
      color: ROOT,
    });
  }
  return out;
}

/** The island's largest centre-to-rim distance (sizes the cloud sea and places distant islands). */
export function islandRadius(plan: IslandPlan): number {
  const { cx, cz } = plan.outline;
  let r = 0;
  for (const p of plan.rim) r = Math.max(r, Math.sqrt((p.x - cx) ** 2 + (p.z - cz) ** 2));
  return r;
}

/** A small distant island: a mossy cap over a tapering rock body, in one vertex-coloured mesh. */
export interface DistantIsland {
  x: number;
  y: number;
  z: number;
  radius: number;
  seed: number;
}

export function distantIslands(plan: IslandPlan, detail: IslandDetail): DistantIsland[] {
  const count = detail.lite ? Math.min(2, plan.wire.distantIslands) : plan.wire.distantIslands;
  if (count <= 0) return [];
  const rng = islandRng(plan.wire.seed ^ 0x9e3779b9);
  const R = islandRadius(plan);
  const start = rng() * Math.PI * 2;
  const out: DistantIsland[] = [];
  for (let k = 0; k < count; k += 1) {
    const a = start + (k / count) * Math.PI * 2 + (rng() - 0.5) * 0.9;
    const d = R + 26 + rng() * 34;
    out.push({
      x: plan.outline.cx + Math.cos(a) * d,
      y: -4 + rng() * 18,
      z: plan.outline.cz + Math.sin(a) * d,
      radius: 5 + rng() * 11,
      seed: (plan.wire.seed + k * 7919) >>> 0,
    });
  }
  return out;
}

/** Merge the distant islands into one vertex-coloured mesh (one draw call). */
export function distantIslandArrays(islands: readonly DistantIsland[], lite: boolean): MeshArrays {
  const seg = lite ? 12 : 20;
  const profile: ReadonlyArray<readonly [number, number]> = lite
    ? [
        [0, 1],
        [0.35, 0.7],
      ]
    : [
        [0, 1],
        [0.12, 0.95],
        [0.4, 0.68],
        [0.7, 0.36],
      ];
  const positions: number[] = [];
  const colors: number[] = [];
  const indices: number[] = [];
  for (const island of islands) {
    const rng = islandRng(island.seed);
    const noise = angularNoise(island.seed ^ 0x85ebca6b);
    const depth = island.radius * (1.1 + rng() * 0.7);
    const base = positions.length / 3;
    // Cap centre.
    positions.push(island.x, island.y + 0.4, island.z);
    colors.push(...MOSS);
    for (let r = 0; r < profile.length; r += 1) {
      const [df, rf] = profile[r] as readonly [number, number];
      for (let i = 0; i < seg; i += 1) {
        const t = (i / seg) * Math.PI * 2;
        const rr =
          island.radius * rf * (0.82 + 0.28 * noise(t)) * (r === 0 ? 1 : 0.92 + rng() * 0.16);
        positions.push(
          island.x + Math.cos(t) * rr,
          island.y - df * depth * (0.8 + 0.3 * noise(t * 2)),
          island.z + Math.sin(t) * rr,
        );
        const c =
          r === 0
            ? MOSS
            : mix3(STRATA[(r + i) % STRATA.length] as readonly [number, number, number], DEEP, df);
        colors.push(c[0], c[1], c[2]);
      }
    }
    // Cap fan (up-facing).
    for (let i = 0; i < seg; i += 1) {
      indices.push(base, base + 1 + ((i + 1) % seg), base + 1 + i);
    }
    // Body rings (outward).
    for (let r = 0; r < profile.length - 1; r += 1) {
      for (let i = 0; i < seg; i += 1) {
        const a = base + 1 + r * seg + i;
        const b = base + 1 + r * seg + ((i + 1) % seg);
        const c = base + 1 + (r + 1) * seg + i;
        const d = base + 1 + (r + 1) * seg + ((i + 1) % seg);
        indices.push(a, d, c, a, b, d);
      }
    }
    // One tip under the body (a ring collapsed to the axis would be seg coincident-ish
    // vertices at different heights: sliver triangles with sideways normals).
    const tip = positions.length / 3;
    positions.push(island.x, island.y - depth, island.z);
    colors.push(...DEEP);
    const last = base + 1 + (profile.length - 1) * seg;
    for (let i = 0; i < seg; i += 1) indices.push(last + i, last + ((i + 1) % seg), tip);
  }
  return {
    positions: Float32Array.from(positions),
    indices: Uint32Array.from(indices),
    colors: Float32Array.from(colors),
  };
}

/**
 * A tileable cloud texture (RGBA8, `size`²): periodic value-noise fbm, so the
 * repeat never seams. Alpha carries the cloud density (the wisp layer reads it;
 * the opaque floor ignores it). Deterministic — no DOM, no canvas.
 */
export function cloudSeaTexture(size: number, seed: number): Uint8Array {
  const rng = islandRng(seed);
  const lattice = 8;
  const octaves = 4;
  const tables: Float32Array[] = [];
  for (let o = 0; o < octaves; o += 1) {
    const period = lattice << o;
    const t = new Float32Array(period * period);
    for (let i = 0; i < t.length; i += 1) t[i] = rng();
    tables.push(t);
  }
  const out = new Uint8Array(size * size * 4);
  for (let y = 0; y < size; y += 1) {
    for (let x = 0; x < size; x += 1) {
      let sum = 0;
      let amp = 0.5;
      let norm = 0;
      for (let o = 0; o < octaves; o += 1) {
        const period = lattice << o;
        const fx = (x / size) * period;
        const fy = (y / size) * period;
        const ix = Math.floor(fx);
        const iy = Math.floor(fy);
        const tx = fx - ix;
        const ty = fy - iy;
        const sx = tx * tx * (3 - 2 * tx);
        const sy = ty * ty * (3 - 2 * ty);
        const t = tables[o] as Float32Array;
        const at = (i: number, j: number): number =>
          t[((j % period) * period + (i % period)) % (period * period)] as number;
        const a = at(ix, iy);
        const b = at(ix + 1, iy);
        const c = at(ix, iy + 1);
        const d = at(ix + 1, iy + 1);
        sum += amp * (a + (b - a) * sx + (c - a) * sy + (a - b - c + d) * sx * sy);
        norm += amp;
        amp *= 0.5;
      }
      const v = sum / norm; // 0..1
      const density = Math.min(1, Math.max(0, (v - 0.36) * 2.4));
      const o4 = (y * size + x) * 4;
      // Cloud tops: blue-grey shadowed troughs to bright crowns. The contrast is wide on
      // purpose: the fog (45..120 m) eats most of it, and a narrow range read as flat grey.
      const light = 0.42 + 0.58 * density;
      out[o4] = Math.round(255 * light);
      out[o4 + 1] = Math.round(255 * Math.min(1, light * 1.02));
      out[o4 + 2] = Math.round(255 * Math.min(1, light * 1.12 + 0.04));
      out[o4 + 3] = Math.round(255 * density);
    }
  }
  return out;
}

/**
 * The cloud sea's levels and extent for a plan. `baseY` is the lowest rim height
 * (0 on flat ground), so on relief the sea hangs below the lowest lip, not the datum.
 */
export interface CloudSeaPlan {
  /** World y of the opaque floor and of the wisp layer (null on the low profile). */
  floorY: number;
  wispY: number | null;
  /** Half-size of the square layers, metres (well past the fog's far plane). */
  halfSize: number;
  cx: number;
  cz: number;
}

export function cloudSeaPlan(
  plan: IslandPlan,
  detail: IslandDetail,
  baseY = 0,
): CloudSeaPlan | null {
  const depth = plan.wire.cloudSeaDepth;
  if (!(depth > 0)) return null;
  return {
    floorY: baseY - depth,
    wispY: detail.lite ? null : baseY - depth * 0.55,
    halfSize: islandRadius(plan) + 520,
    cx: plan.outline.cx,
    cz: plan.outline.cz,
  };
}

/**
 * The ground square the camera actually sees on an island zone: the cloud sea under it
 * (null when the island draws no sea). Past the zone rectangle an island zone shows this
 * sea, not void, so capture framing measures void against it (W44 render, 2026-10-28:
 * the rectangle refused the echo yard at every heading). The sea's extent does not
 * depend on detail, only its wisp layer does.
 */
export function islandFramingExtent(
  plan: IslandPlan,
): { minX: number; minZ: number; maxX: number; maxZ: number } | null {
  const sea = cloudSeaPlan(plan, { lite: true });
  if (!sea) return null;
  return {
    minX: sea.cx - sea.halfSize,
    minZ: sea.cz - sea.halfSize,
    maxX: sea.cx + sea.halfSize,
    maxZ: sea.cz + sea.halfSize,
  };
}

/**
 * Triangles + draw calls the island adds, by detail (the budget estimate's island
 * row). `topTriangles` overrides the ringed-fan top (a relief top's clipped count).
 */
export function islandCost(
  plan: IslandPlan,
  detail: IslandDetail,
  topTriangles?: number,
): { drawCalls: number; triangles: number } {
  const n = plan.rim.length;
  const topTris = topTriangles ?? n + 2 * n * (TOP_RINGS.length - 1);
  const rows = (detail.lite ? UNDERSIDE_PROFILE_LITE : UNDERSIDE_PROFILE_FULL).length;
  const underTris = 2 * n * (rows - 1) + n;
  const rocks = islandRockInstances(plan, () => 0, detail).length;
  const roots = islandRootInstances(plan, () => 0, detail).length;
  const islands = distantIslands(plan, detail);
  const seg = detail.lite ? 12 : 20;
  const ringsD = detail.lite ? 2 : 4;
  const distantTris = islands.length * (seg + 2 * seg * (ringsD - 1) + seg);
  const sea = cloudSeaPlan(plan, detail);
  let drawCalls = 2; // top + underside
  let triangles = topTris + underTris;
  if (rocks > 0) {
    drawCalls += 1;
    triangles += rocks * 20; // IcosahedronGeometry(1, 0)
  }
  if (roots > 0) {
    drawCalls += 1;
    triangles += roots * 10; // ConeGeometry(…, 5 radial)
  }
  if (islands.length > 0) {
    drawCalls += 1;
    triangles += distantTris;
  }
  if (sea) {
    drawCalls += sea.wispY === null ? 1 : 2;
    triangles += sea.wispY === null ? 2 : 4;
  }
  return { drawCalls, triangles };
}
